import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, tasksAPI } from '../services/api';
import TaskCard from './TaskCard';
import TaskDialog from './TaskDialog';
import Settings from './Settings';
import DashboardHeader from './DashboardHeader';
import CategoryColumn from './CategoryColumn';
import JournalSection from './JournalSection';
import { toast } from '../hooks/use-toast';
import { format, startOfDay } from 'date-fns';
import { getTheme } from '../themes';
import { useSettings } from '../hooks/useSettings';
import { useJournal } from '../hooks/useJournal';
import { useTaskSearch } from '../hooks/useTaskSearch';
import { 
  DndContext, 
  DragOverlay, 
  closestCorners, 
  PointerSensor,
  TouchSensor,
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

// Helper function to get local date string in YYYY-MM-DD format
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Dashboard = ({ username, onLogout }) => {
  const [tasks, setTasks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('today');
  const [preSelectedCategory, setPreSelectedCategory] = useState('A');
  const navigate = useNavigate();

  // Custom hooks
  const { showCompletedTasks, layoutMode, showDescription, moveCompletedToBottom, showTaskNumbers, theme } = useSettings();
  const { searchQuery, setSearchQuery, searchResults, isSearching, clearSearch } = useTaskSearch();
  const journal = useJournal(selectedDate, searchQuery);
  const [selectedPriorityCategory, setSelectedPriorityCategory] = useState('A');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const animatingTaskId = useRef(null);
  const tasksRef = useRef(tasks);
  
  // Keep ref in sync with state so drag handlers always read latest
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // Keyboard shortcut: press N to open Add Task dialog (when not typing in an input)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT' || e.target.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (dialogOpen || settingsOpen) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setEditingTask(null);
        setDialogOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialogOpen, settingsOpen]);

  const categories = ['A', 'B', 'C'];
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) < minSwipeDistance) return;
    const currentIndex = categories.indexOf(selectedPriorityCategory);
    if (distance > 0 && currentIndex < categories.length - 1) {
      setSelectedPriorityCategory(categories[currentIndex + 1]);
    } else if (distance < 0 && currentIndex > 0) {
      setSelectedPriorityCategory(categories[currentIndex - 1]);
    }
  };

  const handlePrint = () => {
    const categoryColors = { A: '#b91c1c', B: '#1d4ed8', C: '#15803d' };
    const categoryLabels = { A: 'A High Priority', B: 'B Important', C: 'C No Rush' };
    const categorySubs = { A: 'Important & Urgent', B: 'Important & Not Urgent', C: 'Not Urgent & Not Important' };
    const categoryHeaderBg = { A: '#7f1d1d', B: '#1e3a5f', C: '#14532d' };

    const dateStr = format(selectedDate, 'EEEE, MMMM d, yyyy');
    const cols = ['A', 'B', 'C'].map(cat => {
      const catTasks = getTasksByCategory(cat);
      const numbers = computeVisualNumbers(catTasks);
      const color = categoryColors[cat];
      const tasksHtml = catTasks.length === 0
        ? '<div style="text-align:center;padding:24px 0;color:#999;font-size:10pt;">No tasks</div>'
        : catTasks.map((t, i) => {
          const checked = t.completed;
          const checkmark = checked ? `<span style="color:${color};font-weight:900;font-size:13px;position:absolute;top:-2px;left:1px;">✓</span>` : '';
          const titleStyle = checked ? 'text-decoration:line-through;color:#888;' : 'color:#111;';
          const numberHtml = showTaskNumbers ? `<span style="font-weight:700;color:#333;font-size:10pt;margin-right:4px;">${numbers[i]}.</span>` : '';
          let statusHtml = '';
          if (t.status === 'in-process' || t.status === 'waiting') {
            statusHtml = `<div style="text-align:right;font-style:italic;font-weight:600;color:${color};font-size:9pt;margin-top:2px;">${t.status === 'in-process' ? 'In-Process' : 'Waiting'}</div>`;
          }
          const descHtml = showDescription && t.description ? `<div style="font-size:9pt;color:#555;margin-top:2px;">${t.description.replace(/</g,'&lt;')}</div>` : '';
          return `<div style="border:1px solid #ccc;border-radius:4px;padding:6px 8px;margin-bottom:5px;background:#fff;page-break-inside:avoid;">
            <div style="display:flex;align-items:flex-start;gap:6px;">
              ${numberHtml}
              <div style="width:15px;height:15px;border:2px solid ${color};border-radius:2px;flex-shrink:0;position:relative;margin-top:1px;">${checkmark}</div>
              <span style="font-size:10pt;${titleStyle}flex:1;">${t.title.replace(/</g,'&lt;')}</span>
            </div>
            ${descHtml}${statusHtml}
          </div>`;
        }).join('');

      return `<div style="border:1.5px solid #888;border-radius:6px;padding:8px;box-shadow:3px 4px 10px rgba(0,0,0,0.3);page-break-inside:avoid;">
        <div style="display:inline-block;padding:4px 12px;border-radius:6px;background:${categoryHeaderBg[cat]};color:#fff;font-weight:700;font-size:11pt;margin-bottom:4px;">${categoryLabels[cat]}</div>
        <div style="font-size:9pt;color:#444;font-weight:600;">${categorySubs[cat]}</div>
        <div style="font-size:9pt;color:#444;margin-bottom:6px;">${catTasks.length} task${catTasks.length !== 1 ? 's' : ''}</div>
        ${tasksHtml}
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>TaskPriority Prime - ${dateStr}</title>
      <style>
        @page { size: portrait; margin: 0.5in 0.5in 0.5in 1in; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 0; }
      </style></head><body>
      <div style="text-align:center;padding-bottom:6px;border-bottom:2px solid #333;margin-bottom:8px;">
        <h1 style="font-size:16pt;margin:0;color:#111;">TaskPriority Prime</h1>
        <p style="font-size:9pt;margin:2px 0 0;color:#444;">${dateStr}</p>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;padding-right:12px;">
        ${cols}
      </div>
      <div style="margin-top:14px;border:1.5px solid #888;border-radius:6px;padding:10px 12px;box-shadow:3px 4px 10px rgba(0,0,0,0.3);">
        <div style="font-weight:700;font-size:11pt;color:#111;margin-bottom:6px;">Today's Journal / Notes</div>
        <div style="font-size:10pt;color:#333;white-space:pre-wrap;line-height:1.6;min-height:60px;">${(journal.journalEntry || '').replace(/</g,'&lt;').replace(/>/g,'&gt;') || '<span style="color:#999;font-style:italic;">No notes for today</span>'}</div>
      </div>
    </body></html>`;

    const printWin = window.open('', '_blank', 'width=850,height=1100');
    if (!printWin) {
      toast({ title: 'Popup blocked', description: 'Please allow popups for this site to print.', variant: 'destructive' });
      return;
    }
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => {
      printWin.print();
    }, 250);
  };

  const currentTheme = getTheme(theme);
  
  // Compute display numbers for a sorted category task list.
  // Completed tasks with frozen manual_position keep their number.
  // Active tasks get sequential numbers that SKIP frozen numbers.
  const computeVisualNumbers = (categoryTasks) => {
    const frozenNumbers = new Set(
      categoryTasks.filter(t => t.completed && t.manual_position).map(t => t.manual_position)
    );
    const numbers = [];
    let nextNum = 1;
    for (const task of categoryTasks) {
      if (task.manual_position) {
        numbers.push(task.manual_position);
      } else {
        while (frozenNumbers.has(nextNum)) nextNum++;
        numbers.push(nextNum);
        nextNum++;
      }
    }
    return numbers;
  };

  const categoryInfo = {
    A: {
      title: 'A',
      label: 'High Priority',
      subtitle: 'Important & Urgent',
      color: currentTheme.colors.categoryA,
      headerBg: currentTheme.colors.categoryAHeader,
      textColor: currentTheme.colors.categoryAText,
      titleFont: currentTheme.headingFont || currentTheme.font
    },
    B: {
      title: 'B',
      label: 'Important',
      subtitle: 'Important & Not Urgent',
      color: currentTheme.colors.categoryB,
      headerBg: currentTheme.colors.categoryBHeader,
      textColor: currentTheme.colors.categoryBText,
      titleFont: currentTheme.headingFont || currentTheme.font
    },
    C: {
      title: 'C',
      label: 'No Rush',
      subtitle: 'Not Urgent & Not Important',
      color: currentTheme.colors.categoryC,
      headerBg: currentTheme.colors.categoryCHeader,
      textColor: currentTheme.colors.categoryCText,
      titleFont: currentTheme.headingFont || currentTheme.font
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const isInitialLoad = useRef(true);

  const loadTasks = useCallback(async () => {
    if (isInitialLoad.current) setLoading(true);
    try {
      const userTasks = await tasksAPI.getTasks();
      
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      const today = startOfDay(new Date());
      const todayStr = format(today, 'yyyy-MM-dd');
      const retentionDate = new Date(today);
      retentionDate.setDate(retentionDate.getDate() - 366);
      
      const recentTasks = userTasks.filter(task => {
        if (task.due_date) return new Date(task.due_date + 'T00:00:00') >= retentionDate;
        if (task.completed_date) return new Date(task.completed_date + 'T00:00:00') >= retentionDate;
        return true;
      });
      
      let filteredTasks;
      
      if (viewMode === 'next7days') {
        const endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 6);
        const endDateStr = format(endDate, 'yyyy-MM-dd');
        
        filteredTasks = recentTasks.filter(task => {
          if (task.completed && task.completed_date) {
            const completedDateStr = task.completed_date.split('T')[0];
            return completedDateStr >= todayStr && completedDateStr <= endDateStr;
          }
          return task.due_date && task.due_date >= todayStr && task.due_date <= endDateStr;
        });
      } else if (viewMode === 'allFuture') {
        filteredTasks = recentTasks.filter(task => {
          if (task.completed && task.completed_date) {
            return task.completed_date.split('T')[0] >= todayStr;
          }
          return task.due_date && task.due_date >= todayStr;
        });
      } else {
        filteredTasks = recentTasks.filter(task => {
          if (task.completed && task.completed_date) {
            return task.completed_date.split('T')[0] === selectedDateStr;
          }
          return task.due_date && task.due_date === selectedDateStr;
        });
      }
      
      setTasks(filteredTasks);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load tasks. Please try refreshing.', variant: 'destructive' });
    } finally {
      setLoading(false);
      isInitialLoad.current = false;
    }
  }, [selectedDate, viewMode]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks, username]);

  const handleAddTask = async (taskData) => {
    try {
      const taskDueDate = taskData.dueDate || getLocalDateString(selectedDate);
      const taskCategory = taskData.category || 'A';
      
      const visibleCategoryTasks = getTasksByCategory(taskCategory);
      
      const taskWithDefaults = {
        ...taskData,
        due_date: taskDueDate,
        manual_position: taskData.manualPosition || null,
        order: visibleCategoryTasks.length,
        is_recurring: taskData.isRecurring || false,
        recurrence_frequency: taskData.recurrenceFrequency || null,
      };
      delete taskWithDefaults.dueDate;
      delete taskWithDefaults.manualPosition;
      delete taskWithDefaults.isRecurring;
      delete taskWithDefaults.recurrenceFrequency;
      
      await tasksAPI.createTask(taskWithDefaults);
      await loadTasks();
      toast({ title: 'Task added', description: 'Your task has been created successfully.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add task. Please try again.', variant: 'destructive' });
    }
  };

  const handleCategoryDoubleClick = (category) => {
    setPreSelectedCategory(category);
    setEditingTask(null);
    setDialogOpen(true);
  };

  const handleUpdateTask = async (taskData) => {
    if (!editingTask) return;
    try {
      const dateIsChanging = taskData.dueDate !== editingTask.due_date;
      const categoryIsChanging = taskData.category !== editingTask.category;
      
      const updates = {
        ...taskData,
        due_date: taskData.dueDate,
        manual_position: (dateIsChanging || categoryIsChanging) ? null : taskData.manualPosition,
        is_recurring: taskData.isRecurring,
        recurrence_frequency: taskData.recurrenceFrequency,
      };
      delete updates.dueDate;
      delete updates.manualPosition;
      delete updates.isRecurring;
      delete updates.recurrenceFrequency;
      
      await tasksAPI.updateTask(editingTask.id, updates);
      await loadTasks();
      setEditingTask(null);
      toast({ title: 'Task updated', description: 'Your task has been updated successfully.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update task. Please try again.', variant: 'destructive' });
    }
  };

  const handleToggleComplete = async (id) => {
    try {
      const task = tasks.find(t => t.id === id);
      if (task) {
        const isCompleting = !task.completed;
        
        // Lock this task in place during animation (prevents move-to-bottom)
        animatingTaskId.current = id;

        // Freeze visual number BEFORE optimistic update (uses current sort order)
        if (isCompleting && !task.manual_position) {
          const categoryTasks = getTasksByCategory(task.category);
          const visualNumbers = computeVisualNumbers(categoryTasks);
          const taskIndex = categoryTasks.findIndex(ct => ct.id === task.id);
          if (taskIndex !== -1) {
            tasksAPI.updateTask(id, { manual_position: visualNumbers[taskIndex] });
          }
        }

        // Optimistic local update — task stays in place, checkmark animates
        setTasks(prev => prev.map(t => t.id === id ? {
          ...t,
          completed: isCompleting,
          completed_date: isCompleting ? getLocalDateString() : null,
          status: isCompleting ? 'complete' : 'in-process'
        } : t));

        const result = await tasksAPI.updateTask(id, { 
          completed: isCompleting,
          completed_date: isCompleting ? getLocalDateString() : null,
          status: isCompleting ? 'complete' : 'in-process',
          ...(!isCompleting ? { manual_position: null } : {})
        });
        
        if (isCompleting && result?.next_recurring) {
          toast({ title: 'Next occurrence scheduled', description: `Scheduled for ${result.next_recurring.due_date}` });
        }
        
        // After animation completes, just unlock — don't reload from server
        setTimeout(() => {
          animatingTaskId.current = null;
          // Trigger a re-render to apply moveCompletedToBottom sort
          setTasks(prev => [...prev]);
        }, 600);
      }
    } catch (error) {
      animatingTaskId.current = null;
      await loadTasks();
      toast({ title: 'Error', description: 'Failed to update task. Please try again.', variant: 'destructive' });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const task = tasks.find(t => t.id === id);
      if (task) {
        const isCompleting = newStatus === 'complete';

        // Lock in place during animation
        animatingTaskId.current = id;

        if (isCompleting && !task.manual_position) {
          const categoryTasks = getTasksByCategory(task.category);
          const visualNumbers = computeVisualNumbers(categoryTasks);
          const taskIndex = categoryTasks.findIndex(ct => ct.id === task.id);
          if (taskIndex !== -1) {
            tasksAPI.updateTask(id, { manual_position: visualNumbers[taskIndex] });
          }
        }

        // Optimistic update — stays in place
        setTasks(prev => prev.map(t => t.id === id ? {
          ...t,
          completed: isCompleting,
          completed_date: isCompleting ? getLocalDateString() : null,
          status: newStatus
        } : t));

        const result = await tasksAPI.updateTask(id, { 
          status: newStatus,
          completed: isCompleting,
          completed_date: isCompleting ? getLocalDateString() : null,
          ...(!isCompleting ? { manual_position: null } : {})
        });

        setTimeout(() => {
          animatingTaskId.current = null;
          setTasks(prev => [...prev]);
        }, 600);

        if (isCompleting && result?.next_recurring) {
          toast({ title: 'Next occurrence scheduled', description: `Scheduled for ${result.next_recurring.due_date}` });
        } else if (isCompleting) {
          toast({ title: 'Task completed', description: 'Task marked as complete.' });
        }
      }
    } catch (error) {
      animatingTaskId.current = null;
      await loadTasks();
      toast({ title: 'Error', description: 'Failed to update task status. Please try again.', variant: 'destructive' });
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await tasksAPI.deleteTask(id);
      await loadTasks();
      toast({ title: 'Task deleted', description: 'Your task has been removed.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete task. Please try again.', variant: 'destructive' });
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    onLogout();
  };

  // Shared sort logic: returns tasks for a category in VISUAL order
  const sortCategoryTasks = (allTasks, category) => {
    let filteredTasks = allTasks.filter(task => task.category === category);
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredTasks = filteredTasks.filter(task => 
        task.title.toLowerCase().includes(query) || 
        (task.description && task.description.toLowerCase().includes(query))
      );
    }
    
    if (!showCompletedTasks) {
      filteredTasks = filteredTasks.filter(task => !task.completed);
    }
    
    if (viewMode !== 'today') {
      filteredTasks.sort((a, b) => {
        const dateA = a.due_date || a.completed_date || '';
        const dateB = b.due_date || b.completed_date || '';
        return dateA.localeCompare(dateB);
      });
    } else {
      filteredTasks.sort((a, b) => {
        const orderA = a.order ?? 999999;
        const orderB = b.order ?? 999999;
        return orderA - orderB;
      });
    }
    
    if (moveCompletedToBottom) {
      const originalOrder = filteredTasks.map(t => t.id);
      filteredTasks.sort((a, b) => {
        const aCompleted = a.completed && a.id !== animatingTaskId.current;
        const bCompleted = b.completed && b.id !== animatingTaskId.current;
        if (aCompleted === bCompleted) return originalOrder.indexOf(a.id) - originalOrder.indexOf(b.id);
        return aCompleted ? 1 : -1;
      });
    }
    
    return filteredTasks;
  };

  const getTasksByCategory = (category) => sortCategoryTasks(tasks, category);

  // Drag handlers
  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const currentTasks = tasksRef.current;
    const activeTask = currentTasks.find(t => t.id === active.id);
    if (!activeTask) return;

    const activeCategory = activeTask.category;
    const overId = over.id;
    
    const isPriorityDrop = typeof overId === 'string' && overId.startsWith('priority-drop-');
    if (isPriorityDrop) return;
    
    if (['A', 'B', 'C'].includes(overId)) {
      if (activeCategory !== overId) {
        setTasks(prev => prev.map(t => t.id === active.id ? { ...t, category: overId } : t));
      }
      return;
    }
    
    const overTask = currentTasks.find(t => t.id === overId);
    if (!overTask) return;
    
    if (activeCategory === overTask.category) {
      // Use VISUAL order (same sort the user sees on screen)
      const visualOrder = sortCategoryTasks(currentTasks, activeCategory);
      const oldIndex = visualOrder.findIndex(t => t.id === active.id);
      const newIndex = visualOrder.findIndex(t => t.id === overId);
      
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(visualOrder, oldIndex, newIndex);
        // Assign sequential order values to lock in the new positions
        const reorderedWithOrder = reordered.map((t, i) => ({ ...t, order: i }));
        setTasks(prev => {
          const others = prev.filter(t => t.category !== activeCategory);
          return [...others, ...reorderedWithOrder];
        });
      }
    } else {
      setTasks(prev => prev.map(t => t.id === active.id ? { ...t, category: overTask.category } : t));
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    // Read LATEST tasks from ref, not stale closure
    const currentTasks = tasksRef.current;
    const task = currentTasks.find(t => t.id === active.id);
    if (!task) return;

    const overId = over.id;
    
    // Handle priority button drops
    const priorityMatch = typeof overId === 'string' && overId.match(/^priority-drop-([ABC])$/);
    if (priorityMatch) {
      const targetCategory = priorityMatch[1];
      if (task.category !== targetCategory) {
        try {
          await tasksAPI.updateTask(task.id, { category: targetCategory, order: 999 });
          await loadTasks();
        } catch (error) {
          toast({ title: 'Error', description: 'Failed to move task.', variant: 'destructive' });
        }
      }
      return;
    }

    // Same-category reorder: use VISUAL order (post-dragOver)
    const newCategory = task.category;
    const visualOrder = sortCategoryTasks(currentTasks, newCategory);
    const orderedTasks = visualOrder.map((t, index) => ({ ...t, order: index }));
    
    // Update state — preserve other categories
    setTasks(prev => {
      const others = prev.filter(t => t.category !== newCategory);
      return [...others, ...orderedTasks];
    });
    
    // Persist to server
    try {
      await Promise.all(
        orderedTasks.map(t => tasksAPI.updateTask(t.id, { category: newCategory, order: t.order }))
      );
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save order. Please try again.', variant: 'destructive' });
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={`min-h-screen ${currentTheme.colors.body} ${currentTheme.font} overflow-x-hidden`}>
        <DashboardHeader
          theme={theme}
          currentTheme={currentTheme}
          username={username}
          viewMode={viewMode}
          setViewMode={setViewMode}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onAddTask={() => setDialogOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
          onPrint={handlePrint}
          onLogout={handleLogout}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          clearSearch={clearSearch}
          isSearching={isSearching}
          searchResults={searchResults}
          layoutMode={layoutMode}
          selectedPriorityCategory={selectedPriorityCategory}
          setSelectedPriorityCategory={setSelectedPriorityCategory}
          categoryInfo={categoryInfo}
        />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 relative z-0 print-main"
          onTouchStart={layoutMode === 'priority-stacked' ? onTouchStart : undefined}
          onTouchMove={layoutMode === 'priority-stacked' ? onTouchMove : undefined}
          onTouchEnd={layoutMode === 'priority-stacked' ? onTouchEnd : undefined}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-slate-500">Loading tasks...</p>
            </div>
          ) : (
            <>
              <div className={`grid gap-4 sm:gap-6 ${layoutMode === 'side-by-side' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
              {['A', 'B', 'C'].map(category => {
                const info = categoryInfo[category];
                const categoryTasks = getTasksByCategory(category);
                const taskNumbers = computeVisualNumbers(categoryTasks);
                const isHiddenOnScreen = layoutMode === 'priority-stacked' && category !== selectedPriorityCategory;
                
                return (
                  <div key={category} data-category={category} className={isHiddenOnScreen ? 'hidden print-show' : ''}>
                    <CategoryColumn
                      category={category}
                      info={info}
                      categoryTasks={categoryTasks}
                      taskNumbers={taskNumbers}
                      showTaskNumbers={showTaskNumbers}
                      showDescription={showDescription}
                      viewMode={viewMode}
                      theme={theme}
                      onToggleComplete={handleToggleComplete}
                      onEdit={(task) => {
                        setEditingTask(task);
                        setDialogOpen(true);
                      }}
                      onDelete={handleDeleteTask}
                      onStatusChange={handleStatusChange}
                      onDoubleClick={handleCategoryDoubleClick}
                    />
                  </div>
                );
              })}
            </div>
            </>
          )}

          {!loading && (
            <div className="print-hide">
              <JournalSection
                currentTheme={currentTheme}
                theme={theme}
                journalMatchesSearch={journal.journalMatchesSearch}
                journalEntry={journal.journalEntry}
                setJournalEntry={journal.setJournalEntry}
                journalRef={journal.journalRef}
                handleSaveJournal={journal.handleSaveJournal}
                savingJournal={journal.savingJournal}
              />
            </div>
          )}
          
          <div className="text-center py-4 mt-6 print-hide">
            <p className="text-xs text-gray-400 dark:text-gray-600">&copy; 2026 Robert Farrah</p>
          </div>
        </main>

        <TaskDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingTask(null);
            setPreSelectedCategory('A');
          }}
          onSave={editingTask ? handleUpdateTask : handleAddTask}
          task={editingTask}
          mode={editingTask ? 'edit' : 'add'}
          preSelectedCategory={preSelectedCategory}
          selectedDate={selectedDate}
        />

        <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>

      <DragOverlay>
        {activeTask ? (
          <div className="opacity-80">
            <TaskCard
              task={activeTask}
              onToggleComplete={() => {}}
              onEdit={() => {}}
              onDelete={() => {}}
              onStatusChange={() => {}}
              showDescription={showDescription}
              showDate={viewMode !== 'today'}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default Dashboard;

